// imports
import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2, Divider } from '@withjoy/joykit';
import React, { useCallback, useState, useEffect, useMemo } from 'react';

// local imports
import { useTranslation } from '@shared/core';
import { StarRating } from '@shared/components';
import { LottiePlayer } from '@shared/components';
import { Category, Maybe } from '@graphql/generated';
import { isInIframe } from '@shared/utils/isInIframe';
import { EventVenue } from '@shared/utils/eventPlace';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { SWIPE_RIGHT_LOTTIE_URL } from '@shared/utils/constants';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { useSwipeLeftRight } from '@shared/hooks/useSwipeLeftRight';
import { CTAButton } from '../AccommodationsV2/components/CTAButton';
import { AccommodationPropertyPhotosQuery } from '@graphql/generated';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { JoyPlacePricingMobile } from './components/JoyPlacePricingMobile';
import { hasAccommodationLink } from '../Accommodations/Accommodation.utils';
import { InterestedGuestsLabel } from '../AccommodationsV2/AccommodationsTileV2';
import { ReservationsCount } from '../AccommodationsV2/components/ReservationsCount';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog/ResponsiveDialog';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { UserRating } from '@apps/accommodations/components/ListingsPanel/components/Ratings';
import { TravelLogistics } from '@apps/accommodations/components/ListingsPanel/components/TravelLogistics';
import { AccommodationCombined, AccommodationCombinedType } from '../Accommodations/Accommodation.types';
import { AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { useHotelUserRating } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelUserRating';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { label3ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';

// interface
export interface AccommodationsMobileDialogProps {
  isOpen: boolean;
  eventHandle: string;
  onClose: () => void;
  locationInfo: EventVenue;
  selectedAccommodationId?: string | null;
  accommodations: AccommodationCombined[];
  accommodationPropertyPhotos: AccommodationPropertyPhotosQuery | undefined;
  getReservationsCountData?: (joyPlaceId?: Maybe<string>) => number | null | undefined;
}

export const AccommodationsMobileDialog = (props: AccommodationsMobileDialogProps) => {
  const inPreviewMode = isInIframe();
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const { t: guestSiteTranslation } = useTranslation('guestSiteCustom');

  const accTrans = guestSiteTranslation('accommodations');

  const { isOpen, onClose, eventHandle, locationInfo, accommodations, selectedAccommodationId, accommodationPropertyPhotos, getReservationsCountData } = props;

  const initialIndex = accommodations.findIndex(accommodation => accommodation.id === selectedAccommodationId);

  const [currentIndex, setCurrentIndex] = useState(initialIndex >= 0 ? initialIndex : 0);
  const [isSwipeInProgress, setIsSwipeInProgress] = useState(false);

  useEffect(() => {
    const newIndex = accommodations.findIndex(accommodation => accommodation.id === selectedAccommodationId);
    if (newIndex >= 0) {
      setCurrentIndex(newIndex);
    }
  }, [selectedAccommodationId, accommodations]);

  const navigateAccommodation = useCallback(
    (direction: 'next' | 'previous') => {
      setCurrentIndex(prevIndex => {
        if (direction === 'next') {
          return (prevIndex + 1) % accommodations.length;
        }
        return (prevIndex - 1 + accommodations.length) % accommodations.length;
      });
    },
    [accommodations.length],
  );

  const swipeHandlers = useSwipeLeftRight(
    { minSwipeDistance: 50 },
    () => navigateAccommodation('next'),
    () => navigateAccommodation('previous'),
  );

  const customSwipeHandlers = useMemo(
    () => ({
      onTouchStart: (e: React.TouchEvent) => {
        setIsSwipeInProgress(true);
        swipeHandlers.onTouchStart(e);
      },
      onTouchMove: (e: React.TouchEvent) => {
        swipeHandlers.onTouchMove(e);
      },
      onTouchEnd: () => {
        setIsSwipeInProgress(false);
        swipeHandlers.onTouchEnd();
      },
    }),
    [swipeHandlers],
  );

  const currentAccommodation = useMemo(() => {
    return accommodations[currentIndex] || accommodations[0];
  }, [accommodations, currentIndex]);

  const {
    reviewCount,
    validatedRating,
    loading: fetchingUserHotelRating,
  } = useHotelUserRating({
    hotel: {
      id: currentAccommodation?.id || '',
      zhId: currentAccommodation?.zhId,
      type: currentAccommodation?.type,
      joyPlaceId: currentAccommodation?.joyPlaceId,
    },
    context: 'guest accommodation mobile dialog user rating',
  });

  const [showPricingEnabled] = useIsFeatureEnabled('travelShowHotelPrice');
  const [accommodationsMyOwnLinkEnabled] = useIsFeatureEnabled('accommodationsMyOwnLinkEnabled');
  const { ctaTitle, ctaUrl } = useAccommodationsCta({ eventHandle, accommodation: currentAccommodation, locationInfo });

  const renderTagComponent = useCallback(() => {
    const reservationCount = getReservationsCountData?.(currentAccommodation?.joyPlaceId) ?? 0;

    if (reservationCount > 0) {
      return <ReservationsCount reservationsCount={reservationCount} />;
    } else if (currentAccommodation?.interestedGuestCount && currentAccommodation.interestedGuestCount > 1) {
      return <InterestedGuestsLabel interestedGuestCount={currentAccommodation.interestedGuestCount} />;
    }

    return null;
  }, [getReservationsCountData, currentAccommodation?.joyPlaceId, currentAccommodation?.interestedGuestCount]);

  const renderRoomBlockPricingSection = useCallback(() => {
    if (!currentAccommodation || currentAccommodation.type !== AccommodationCombinedType.Roomblock) return null;

    const { pricePerNight } = currentAccommodation;

    return (
      <Box marginY={pxToRem(12)}>
        <Flex
          justifyContent="flex-end"
          columnGap={2}
          marginBottom={pxToRem(10)}
          alignItems="center"
        >
          <TextV2 typographyVariant="hed6">${pricePerNight}</TextV2>
          <TextV2
            typographyVariant="label2"
            color="mono10"
          >
            /night
          </TextV2>
        </Flex>
        <TextV2
          typographyVariant="label1"
          textAlign="right"
        >
          {accTrans.exclusiveBlockRate()}
        </TextV2>
      </Box>
    );
  }, [currentAccommodation, accTrans]);

  const createAccommodationListing = useCallback((): AccommodationListingDTO => {
    const baseListing = {
      facilities: [],
      userRating: null,
      name: currentAccommodation.displayName || '',
      location: {
        lat: currentAccommodation.latitude ?? 0,
        lng: currentAccommodation.longitude ?? 0,
      },
      address: {
        city: currentAccommodation.city || null,
        street: currentAccommodation.address || null,
        country: currentAccommodation.country || null,
      },
      id: currentAccommodation.joyPlaceId as string,
      userNote: currentAccommodation.description || null,
      starRating: currentAccommodation.starRating || null,
      joyPlaceId: currentAccommodation.joyPlaceId as string,
      mediaAssets: {
        isLoading: false,
        photos: currentAccommodation.photo?.url ? [{ url: currentAccommodation.photo.url }] : [],
      },
    };

    if (currentAccommodation.type === AccommodationCombinedType.Roomblock) {
      return {
        ...baseListing,
        pricingInfo: {
          values: null,
          isLoading: false,
        },
        isFavorite: false,
        type: 'roomBlock' as const,
        cutoffDate: currentAccommodation.cutoffDate || '',
        interestedGuestCount: currentAccommodation.interestedGuestCount || 0,
        numberOfRoomsReserved: currentAccommodation.numberOfRoomsReserved || 0,
      };
    } else {
      return {
        ...baseListing,
        pricingInfo: {
          values: null,
          isLoading: false,
        },
        isSoldOutForSelectedDates: false,
        isFavorite: false,
        type: 'standardHotel' as const,
      };
    }
  }, [currentAccommodation]);

  if (!currentAccommodation || accommodations.length === 0) {
    onClose();
    return null;
  }

  const finalRating = validatedRating;
  const finalReviewCount = reviewCount;
  const { displayName } = currentAccommodation;

  const propertyPhotos = accommodationPropertyPhotos?.searchAccommodationProperties?.properties?.find(property => property.joyPlaceId === currentAccommodation.joyPlaceId)
    ?.photos ?? [{ url: currentAccommodation.photo?.url as string }];

  const showRegularHotelPricing = !currentAccommodation.customUrl && showPricingEnabled && !inPreviewMode;

  const hasLink = hasAccommodationLink(
    accommodationsMyOwnLinkEnabled,
    currentAccommodation.isCustomLinkEnabled,
    currentAccommodation.customUrl,
    currentAccommodation.url,
    currentAccommodation.zhId,
  );

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider={true}
      hasCloseButton={false}
      removeBodyPadding={true}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 0, paddingX: 0 } },
      }}
      dialogOverrides={{
        Body: { props: { paddingX: 0, paddingY: 0 } },
        Content: { props: { width: '100%', maxWidth: 620 } },
      }}
    >
      <Flex
        flexDirection="column"
        gap={7}
        alignItems="center"
      >
        <Flex
          gap={4}
          width="100%"
          flexDirection="column"
          onTouchEnd={customSwipeHandlers.onTouchEnd}
          onTouchMove={customSwipeHandlers.onTouchMove}
          onTouchStart={customSwipeHandlers.onTouchStart}
          paddingBottom={pxToRem(24)}
          gridTemplateColumns="1fr 2fr"
          display={{ _: 'flex', sm2: 'grid' }}
          paddingX={{ _: 5, sm2: pxToRem(24) }}
        >
          <Flex
            marginTop={pxToRem(24)}
            gap={4}
            alignItems="center"
            justifyContent="space-between"
          >
            <Close onClick={onClose} />
            <Flex
              alignItems="center"
              gap={2}
            >
              <Flex
                alignItems="flex-end"
                width={pxToRem(56)}
                height={pxToRem(56)}
                paddingBottom={pxToRem(5)}
              >
                <LottiePlayer
                  play={!isSwipeInProgress}
                  loop
                  lottieUrl={SWIPE_RIGHT_LOTTIE_URL}
                />
              </Flex>
              <TextV2 typographyVariant="label2">{accTrans.swipeRightForQuickView()}</TextV2>
            </Flex>
          </Flex>

          <Box
            width={'100%'}
            position="relative"
          >
            <Flex
              justifyContent="space-between"
              position="absolute"
              top={4}
              left={4}
              right={4}
              zIndex={1}
              flexWrap="wrap"
              gap={4}
            >
              <Box
                width={32}
                height={32}
                columnGap={2}
                display={'flex'}
                borderRadius="50%"
                alignItems="center"
                justifyContent="center"
                backdropFilter={'blur(4px)'}
                backgroundColor={`rgba(247, 247, 247, 0.6)`}
              >
                <GuestSiteTypographyOverride
                  override={label3ToParagraphOverride}
                  defaultFontFamily="Inter UI"
                >
                  <TextV2
                    typographyVariant="label3"
                    fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                    fontWeight={500}
                    fontFamily="Inter UI"
                    color="mono14"
                  >
                    {currentIndex + 1}
                  </TextV2>
                </GuestSiteTypographyOverride>
              </Box>
              {renderTagComponent()}
            </Flex>
            <Box
              width="100%"
              aspectRatio="3/2"
              overflow={'hidden'}
              position={'relative'}
              maxHeight={pxToRem(206)}
              backgroundColor={'mono2'}
              borderRadius={pxToRem(12)}
              onTouchEnd={e => e.stopPropagation()}
              onTouchMove={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
            >
              <PhotoCarousel
                aspectRatio="3/2"
                navigation={{ variant: 'minimal', visible: 'default' }}
                images={propertyPhotos.map(photo => ({ src: photo.url, alt: currentAccommodation.displayName || '' }))}
              />
            </Box>
          </Box>
          <Flex
            alignItems="center"
            gap={5}
            marginY={pxToRem(14)}
          >
            <TextV2 typographyVariant="hed3">{displayName}</TextV2>
            <StarRating
              rating={currentAccommodation.starRating ?? 0}
              id={currentAccommodation.id}
              hideNonFilledStars
            />
          </Flex>
          {locationInfo.latitude != null && locationInfo.longitude != null && (
            <TravelLogistics
              useShortWording={true}
              venueCoordinates={{
                latitude: locationInfo.latitude,
                longitude: locationInfo.longitude,
              }}
              listing={createAccommodationListing()}
            />
          )}
          <Box marginBottom={pxToRem(5)}>
            {fetchingUserHotelRating ? (
              <UserRating.Skeleton />
            ) : (
              <UserRating
                rating={finalRating}
                reviewCount={finalReviewCount}
              />
            )}
          </Box>
          <Divider />
          {renderRoomBlockPricingSection()}
          {currentAccommodation.type === AccommodationCombinedType.Hotel && showRegularHotelPricing ? (
            <JoyPlacePricingMobile accommodationPlaceId={currentAccommodation.id} />
          ) : null}
          {hasLink && (
            <CTAButton
              width="100%"
              variant="solid"
              color="primary1"
              ctaTitle={ctaTitle}
              backgroundColor="#4951EF"
              typographyVariant="button2"
              {...(hasLink && { as: 'a', href: ctaUrl, target: '_blank' })}
            />
          )}
        </Flex>
      </Flex>
    </ResponsiveDialog>
  );
};
